import { PageRef } from 'providers/page-ref';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { FitnessState } from '@cf-shared/data-model/user-state';

export type ExerciseConfigurationPageParams = {
  subSlotId: string;
  returnCallback?: string;
  /**
   * The caller can save the coach's parts of the page (#6428): Edit Workouts in its edit mode, and
   * the Training Overview route. The page still shows them only in manage mode, outside an admin
   * snapshot, and for a program entry that already has its intensity days. Anything but `true`
   * counts as not set. See design-docs/coach-exercise-editing.md.
   */
  coachEditable?: boolean;
  /** Complex objects stored in sessionStorage, not URL */
  local?: {
    fitnessPlan: FitnessPlan;
    fitnessState: FitnessState;
    returnContext?: unknown;
  };
};

/**
 * Handed back only when the coach's parts were shown and the coach changed something (#6428). The
 * caller's save runs the shared save step for this sub-slot only when it is present, so an
 * ordinary user's edit never gets a coach stamp, a change record or a note to the client.
 */
export type CoachExerciseEditResult = {
  subSlotId: string;
  /** true when a scheme switch (or Calculate Weights) built the paths in this visit */
  pathsRebuiltBySwitch: boolean;
  /**
   * The Sets count the page showed when it opened and the count the person left, present only when
   * they differ. Decided from what the person saw, because leaving the field rewrites the stored base
   * even when the count stays the same, and how the shown count is made from the base differs for a
   * myo-rep exercise.
   */
  volume?: { before: number; after: number };
};

export type ExerciseConfigurationPageResult = {
  modified: boolean;
  saved: boolean;
  fitnessPlan: FitnessPlan;
  fitnessState: FitnessState;
  coachEdit?: CoachExerciseEditResult;
};

export const IExerciseConfigurationPage: PageRef<ExerciseConfigurationPageParams, ExerciseConfigurationPageResult> = {
  pageName: 'ExerciseConfigurationPage',
} as PageRef<ExerciseConfigurationPageParams, ExerciseConfigurationPageResult>;
